
























import { computed, defineComponent, ref, set, watch } from "@vue/composition-api";
import { InputMassMessageSearch, MassMessage } from "@scrile/api-provider/dist/api/MassMessageProvider";
import useAuthUser from "@/hooks/useAuthUser";
import { useGroups } from "@/hooks/useGroups";
import usePager from "@/hooks/usePager";
import providers from "@/lib/api";
import BasePage from "@/components/BasePage";
import EmptyContent from "@/components/EmptyContent";
import InfiniteScroll from "@/components/InfiniteScroll";
import MassMessageEdit from "@/components/MassMessageEdit";
import PageTitle from "@/components/PageTitle";
import { RecipientsCountMap } from "@/views/PageScheduledMassMessage/type";
import ScheduledMassMessageStatView from "./components/ScheduledMassMessageStatView";
import usePrice from "./hooks/usePrice";

export default defineComponent({
  name: "PageScheduledMassMessage",
  components: { MassMessageEdit, InfiniteScroll, BasePage, PageTitle, EmptyContent, ScheduledMassMessageStatView },
  setup() {
    const { authUserStrict } = useAuthUser();
    const { paymentPackageMap } = usePrice();
    const { groups, getMyGroups } = useGroups();

    const loading = ref(false);
    const massMessageList = ref<MassMessage[]>([]);
    const editableMassMessage = ref<MassMessage | null>(null);

    const recipientsCountMap = ref<RecipientsCountMap>({});

    const getRecipientsCount = async (messages: MassMessage[]) => {
      const countMap: RecipientsCountMap = {};
      await Promise.all(
        messages.map(message => {
          return providers.GroupsProvider.uniqueMemberCount({
            data: { groupIds: message.groupIds, excludeGroupIds: message.excludedGroupIds },
          }).then(response => {
            countMap[message.id] = response;
          });
        })
      );

      recipientsCountMap.value = { ...recipientsCountMap.value, ...countMap };
    };

    const { enableScroll, items, getNextPage, getFirstPage, removeItem } = usePager<
      MassMessage,
      InputMassMessageSearch
    >(
      (page, requestData) =>
        providers.MassMessageProvider.search({
          data: {
            senderId: authUserStrict.value.id,
            page,
            size: 20,
            sort: "PUBLISH_TIME,ASC",
            ...requestData,
          },
        }),
      messages => {
        getRecipientsCount(messages);
      }
    );

    const isEditMode = computed(() => !!editableMassMessage.value);
    const onEdit = (massMessage: MassMessage) => {
      editableMassMessage.value = massMessage;
    };

    const onEditBack = (massMessage: MassMessage) => {
      editableMassMessage.value = null;
      const isScheduledMessage = new Date(massMessage.publishTime).getTime() >= new Date().getTime() + 1000;
      if (isScheduledMessage) {
        const index = massMessageList.value.findIndex(m => m.id === massMessage.id);
        if (index === -1) return;
        set(massMessageList.value, index, massMessage);
      } else removeItem(i => i.id === massMessage.id);
    };

    const onMessageDelete = (messageId: string) => {
      removeItem(i => i.id === messageId);
      editableMassMessage.value = null;
    };

    const getData = () => {
      loading.value = true;
      Promise.all([getFirstPage(), getMyGroups()]).finally(() => (loading.value = false));
    };
    getData();

    watch(items, () => (massMessageList.value = items.value), { immediate: true });
    return {
      groups,
      loading,
      isEditMode,
      getNextPage,
      enableScroll,
      massMessageList,
      paymentPackageMap,
      recipientsCountMap,
      editableMassMessage,
      onEdit,
      onEditBack,
      onMessageDelete,
    };
  },
});
